import React, { useCallback, useState, useEffect } from 'react';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { scrollCommentIntoView } from '@confluence/comments-util';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { useInlineCommentQueryParams } from '@confluence/comment';
import { useInlineCommentsState } from '@confluence/inline-comments-hooks';
import {
	useInlineCommentsContext,
	useInlineCommentsDispatchContext,
	useCommentContentContext,
	useCommentContentDispatchContext,
} from '@confluence/comment-context';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import {
	GeneralShortcutListener,
	NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
} from '@confluence/shortcuts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter';

type ViewInlineCommentShortcutListenerProps = {
	isFabricPage: boolean;
	contentId?: string;
};

const ViewInlineCommentShortcutListenerComponent = ({
	isFabricPage,
	contentId,
}: ViewInlineCommentShortcutListenerProps) => {
	const { navigateToComment } = useInlineCommentsDispatchContext();
	const { activeHighlight } = useInlineCommentsContext();
	const { hasContentChanged } = useCommentContentContext();
	const { resetContentChanged } = useCommentContentDispatchContext();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();
	const { unresolvedInlineComments } = useInlineCommentsState();
	const emitter = getRendererAnnotationEventEmitter();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isNextCommentLoading, setIsNextCommentLoading] = useState(false);

	useEffect(() => {
		setIsNextCommentLoading(false);
	}, [activeHighlight]);

	const getCommentIndex = useCallback(
		(currentMarkerRef: string): number => {
			return unresolvedInlineComments?.findIndex((markerRef) => markerRef === currentMarkerRef);
		},
		[unresolvedInlineComments],
	);

	const openInlineComment = useCallback(
		(markerRef) => {
			const commentElement = document.getElementById(markerRef);
			emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: [markerRef],
				eventTarget: commentElement,
			});
			scrollCommentIntoView({ commentElement, isFabricPage: true });
		},
		[emitter],
	);

	const onNavigationClick = useCallback(
		(nextMarkerRef: string) => {
			if (hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						openInlineComment(nextMarkerRef);
					},
				});
			} else {
				openInlineComment(nextMarkerRef);
			}
		},
		[hasContentChanged, resetContentChanged, showModal, openInlineComment],
	);

	const handleInlineCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			const annotations: HTMLElement[] = [].slice.call(
				document.querySelectorAll(`[data-mark-annotation-type="inlineComment"]`),
			);
			const firstInlineCommentMarkerRef = annotations.find(
				(item) => item.getAttribute('data-mark-annotation-state') === 'active', //active means it's open (i.e. not resolved)
			)?.id;

			if (activeHighlight) {
				//Check if the next inline comment is still in process of being rendered. Otherwise, quickly pressing keys will force comment to open and close instead of navigating
				if (isNextCommentLoading) return;

				setIsNextCommentLoading(true);
				navigateToComment({
					action,
					triggeredByKeyboardShortcut: true,
					currentCommentIndex: getCommentIndex(activeHighlight),
					onNavigationClick,
					isEditor: false,
					removeCommentQueryParams,
					unresolvedInlineComments,
					createAnalyticsEvent,
					contentId,
				});
			} else {
				openInlineComment(firstInlineCommentMarkerRef);
			}
		},
		[
			activeHighlight,
			onNavigationClick,
			removeCommentQueryParams,
			unresolvedInlineComments,
			createAnalyticsEvent,
			contentId,
			getCommentIndex,
			navigateToComment,
			isNextCommentLoading,
			openInlineComment,
		],
	);

	if (!isFabricPage) return null;

	return (
		<div data-testId="comments-shortcut-listener">
			<GeneralShortcutListener
				accelerator={NEXT_COMMENT_SHORTCUT}
				listener={() => handleInlineCommentNavigation('next')}
			/>
			<GeneralShortcutListener
				accelerator={PREV_COMMENT_SHORTCUT}
				listener={() => handleInlineCommentNavigation('previous')}
			/>
		</div>
	);
};

export const ViewInlineCommentShortcutListener = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(ViewInlineCommentShortcutListenerComponent);
